import React from 'react';
import { Button } from '../../../components/ui/Button';
import { AppIcon } from '../../../components/icons/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { blockerConfig, type BlockerStatus } from './blockerConfig';

interface SubscriptionBlockerProps {
    status: BlockerStatus;
    daysUntilBlock?: number;
    canManageBilling?: boolean;
    onNavigateToBilling: () => void;
}

const SubscriptionBlocker: React.FC<SubscriptionBlockerProps> = ({
    status,
    daysUntilBlock,
    canManageBilling = true,
    onNavigateToBilling,
}) => {
    const { logout } = useAuth();
    const config = blockerConfig[status] || blockerConfig.blocked;

    return (
        <div 
            className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-md transition-all duration-300"
            role="dialog"
            aria-modal="true"
            aria-labelledby="blocker-title"
        >
            <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-border/10 bg-card text-card-foreground shadow-modal transition-all duration-300 animate-in fade-in zoom-in-95 duration-200">
                {/* Header com Gradiente Premium */}
                <header className={`relative flex flex-col items-center justify-center bg-gradient-to-br ${config.gradient} px-6 py-10 text-center text-white`}>
                    {/* Elemento de brilho decorativo em CSS Tailwind v4 */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-white/10 to-transparent pointer-events-none" />

                    {/* Logo/Icon Container */}
                    <div className="relative z-10 mb-5">
                        {config.icon === 'none' ? (
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-card p-2.5 shadow-lg ring-4 ring-white/10 transition-transform duration-300 hover:scale-105">
                                <img 
                                    src="/colpasado%2048x48.png" 
                                    alt="Adlitics Logo" 
                                    className="h-12 w-12 object-contain" 
                                />
                            </div>
                        ) : (
                            <div className={`flex h-20 w-20 items-center justify-center rounded-full ${config.iconBg} shadow-lg ring-4 ring-white/10 transition-transform duration-300 hover:scale-105`}>
                                <AppIcon name={config.icon} className={`h-10 w-10 ${config.iconColor}`} />
                            </div>
                        )}
                    </div>

                    {/* Título com fonte semântica e espaçamento */}
                    <h2 
                        id="blocker-title" 
                        className="relative z-10 font-sans text-heading-lg text-white mb-2"
                    >
                        {config.title}
                    </h2>

                    {/* Alerta de dias restantes para past_due */}
                    {status === 'past_due' && daysUntilBlock !== undefined && daysUntilBlock > 0 && (
                        <div className="relative z-10 my-2 inline-flex items-center gap-1.5 rounded-full bg-card/15 px-3 py-1 text-xs font-semibold text-white/95 backdrop-blur-sm">
                            <span aria-hidden="true">⏱️</span> {daysUntilBlock} dia{daysUntilBlock > 1 ? 's' : ''} para regularizar
                        </div>
                    )}

                    {/* Descrição */}
                    <p className="relative z-10 text-sm font-medium text-white/80 max-w-xs leading-relaxed">
                        {config.description}
                    </p>
                </header>

                {/* Conteúdo Principal */}
                <main className="p-6 md:p-8 bg-card">
                    {/* Lista de Recursos (se aplicável) */}
                    {config.features && (
                        <div className="mb-6 rounded-xl bg-surface/50 border border-border/10 p-5">
                            <ul className="space-y-3.5" role="list">
                                {config.features.map((feature, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm font-medium text-text-secondary leading-tight">
                                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-subtle text-accent-dark">
                                            <AppIcon name="check" className="h-3.5 w-3.5" />
                                        </span>
                                        <span className="pt-0.5">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Ações */}
                    <div className="flex flex-col gap-3">
                        {canManageBilling ? (
                            <Button 
                                size="lg" 
                                className="w-full justify-center gap-2 rounded-xl bg-primary hover:bg-primary-dark text-white font-semibold shadow-lg hover:shadow-primary/20 transition-all duration-200 cursor-pointer"
                                onClick={onNavigateToBilling}
                            >
                                <AppIcon name="credit-card" className="h-5 w-5" />
                                {config.buttonText}
                            </Button>
                        ) : (
                            <p className="text-center text-sm text-text-secondary leading-relaxed px-2">
                                Entre em contato com o administrador ou proprietário da organização para regularizar o acesso.
                            </p>
                        )}

                        {(config.showLogout || !canManageBilling) && (
                            <Button 
                                variant="ghost" 
                                size="default" 
                                className="w-full justify-center gap-2 rounded-xl text-text-muted hover:bg-red-500/10 hover:text-red-600 transition-colors duration-200 font-medium cursor-pointer"
                                onClick={logout}
                            >
                                <AppIcon name="logout" className="h-4 w-4" />
                                Sair da Conta
                            </Button>
                        )}
                    </div>
                </main>

                {/* Rodapé do Modal */}
                <footer className="border-t border-border/10 bg-surface/20 px-6 py-4 text-center">
                    <p className="text-xs text-text-muted font-medium">
                        Precisa de ajuda?{' '}
                        <a 
                            href="mailto:suporte@adlitics.com" 
                            className="text-primary hover:text-primary-dark hover:underline font-semibold transition-colors duration-200"
                        >
                            Entre em contato
                        </a>
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default SubscriptionBlocker;
